import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Observable, catchError } from 'rxjs'

import { environment } from '../../environments/environment'
import { ErrorService } from './error.service'
import {
  CloneReportResponse,
  FprReport,
  FprPlasticRow,
  FprResinRow,
  ReportResponse,
  FprRows,
  ExemptionCheckResponse,
  ImportReportResponse,
  MissingProductsSummary,
  ProvisionalRow,
} from '../types/reports'
import { FPR_QUANTITY_KEYS, FprQuantityKey } from '../constants/reports'
import {
  PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT,
  PRODUCT_MATERIAL_TYPE_RAW_RESIN,
} from '../constants/plastics-declaration'

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'reports'

  /**
   * Sends request to generate editable rows for an FPR report.
   * @param {number} year Reporting year (e.g., 2024)
   * @param {string[]} [tags] Optional product tags to filter the generated report rows
   * @returns {Observable<ReportResponse>} Report metadata and generated rows
   */
  generateReportRows(
    year: number,
    tags: string[] = [],
  ): Observable<ReportResponse> {
    const url = `${this.URL}/generate`
    let params = new HttpParams().set('year', String(year))

    for (const tag of tags) {
      params = params.append('tags', tag)
    }

    return this.http
      .post<ReportResponse>(url, {}, { params })
      .pipe(catchError(this.errorService.handleError))
  }

  getAll(): Observable<Array<FprReport>> {
    return this.http
      .get<Array<FprReport>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Fetch a single report (metadata + merged rows: DB overlaid with Redis drafts)
   * @param reportId Report ID (UUID)
   */
  getReport(
    reportId: string,
  ): Observable<{ report: FprReport; rows: FprRows }> {
    const url = `${this.URL}/${reportId}`
    return this.http
      .get<{ report: FprReport; rows: FprRows }>(url)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Patch report rows (partial updates) into Redis drafts
   * @param {string} reportId Report ID
   * @param patches Array of patches for plastics or resins
   */
  patchReportRows(
    reportId: string,
    patches: Array<{ id: string; fields: Partial<FprPlasticRow> }>,
  ): Observable<void>
  patchReportRows(
    reportId: string,
    patches: Array<{ id: string; fields: Partial<FprResinRow> }>,
  ): Observable<void>
  patchReportRows(
    reportId: string,
    patches: Array<{
      id: string
      fields: Partial<FprPlasticRow> | Partial<FprResinRow>
    }>,
  ): Observable<void> {
    const url = `${this.URL}/${reportId}/rows`
    return this.http
      .patch<void>(url, patches)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Patch only Plastic rows.
   */
  patchPlasticRows(
    reportId: string,
    patches: Array<{ id: string; fields: Partial<FprPlasticRow> }>,
  ): Observable<void> {
    return this.patchRows(reportId, patches)
  }

  /**
   * Patch only Resin rows.
   */
  patchResinRows(
    reportId: string,
    patches: Array<{ id: string; fields: Partial<FprResinRow> }>,
  ): Observable<void> {
    return this.patchRows(reportId, patches)
  }

  /** Internal helper to avoid duplicating the HTTP call. */
  private patchRows(
    reportId: string,
    patches: Array<{
      id: string
      fields: Partial<FprPlasticRow> | Partial<FprResinRow>
    }>,
  ): Observable<void> {
    const url = `${this.URL}/${reportId}/rows`
    return this.http
      .patch<void>(url, patches)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Flush Redis drafts to DB for a report (still keeps drafts in Redis)
   * @param {string} reportId Report id
   */
  saveReport(reportId: string): Observable<void> {
    const url = `${this.URL}/${reportId}/save`
    return this.http
      .post<void>(url, {})
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Kick off background publishing for a report.
   * Sets status to "publishing" and starts the async job (expects HTTP 202).
   */
  publishReport(reportId: string): Observable<void> {
    const url = `${this.URL}/${reportId}/publish`
    return this.http
      .post<void>(url, {})
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Download the published XLSX report as a Blob
   * @param {string} reportId Report ID
   */
  downloadReport(reportId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.URL}/${reportId}/download`
    return this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Clone a report into a new in‑progress version
   * @param {string} reportId Source report ID
   * @returns {CloneReportResponse} Observable with the new report's id and name
   */
  cloneReport(reportId: string): Observable<CloneReportResponse> {
    const url = `${this.URL}/${reportId}/clone`
    return this.http
      .post<{ id: string; name: string }>(url, {})
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Import quantities for an FPR report from an .xlsx file using a saved FPR Template mapping.
   * When reportId is not provided, this creates a new in-progress report for the given year.
   * When reportId is provided, this updates an existing report by applying the uploaded quantities
   * according to the selected merge mode (overwrite or add).
   *
   * @param {string} templateId   ID of the FPR Template to apply
   * @param {number | null} year  Reporting year (required when creating a new report; ignored when updating an existing report)
   * @param {File} file           XLSX file to upload
   * @param {'overwrite' | 'add'} mergeMode  How to apply quantities for matching rows
   * @param {string[]} [tags]     Optional product tags to associate with a newly created report
   * @param {string} [reportId]   Optional report ID to update instead of creating a new report
   * @returns {Observable<ImportReportResponse>}   Report metadata, parsed rows, and missing products summary
   */
  importReportFromSpreadsheet(
    templateId: string,
    year: number | null,
    file: File,
    mergeMode: 'overwrite' | 'add',
    tags: string[] = [],
    reportId?: string,
  ): Observable<ImportReportResponse> {
    const url = `${this.URL}/import`
    const form = new FormData()

    if (typeof year === 'number') {
      form.append('year', String(year))
    }
    form.append('templateId', templateId)
    form.append('file', file, file.name)
    form.append('mergeMode', mergeMode)

    if (reportId) {
      form.append('reportId', reportId)
    }

    for (const tag of tags) {
      form.append('tags', tag)
    }

    return this.http
      .post<ImportReportResponse>(url, form)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Download the current raw workbook (raw XLSX) for a report as a Blob.
   * - If the report is published, the server streams the persisted raw copy from S3.
   * - If the report is in progress, the server builds the workbook from Redis drafts and streams it.
   * @param {string} reportId Report ID
   * @returns {Observable<HttpResponse<Blob>>} Full HTTP response with Blob body (so callers can read headers like Content-Disposition)
   */
  downloadRaw(reportId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.URL}/${reportId}/download-raw`
    return this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Check whether a report meets the exemption threshold.
   * Returns aggregated kilograms (plastics/resins/total), the threshold, and a qualifies flag.
   * @param {string} reportId Report ID (
   * @returns {Observable<ExemptionCheckResponse>} Aggregated totals and exemption decision
   */
  checkExemption(reportId: string): Observable<ExemptionCheckResponse> {
    const url = `${this.URL}/${reportId}/exemption-check`
    return this.http
      .get<ExemptionCheckResponse>(url)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Fetch a summary of products that are eligible for a given report year
   * but are not yet present in the report.
   * @param {string} reportId Report ID
   */
  getMissingProducts(reportId: string): Observable<MissingProductsSummary> {
    const url = `${this.URL}/${reportId}/missing-products`
    return this.http
      .get<MissingProductsSummary>(url)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Hydrate the current report with any missing products for its reporting year.
   * The backend will add rows for those products into the report's drafts.
   * @param {string} reportId Report ID
   */
  hydrateMissingProducts(reportId: string): Observable<void> {
    const url = `${this.URL}/${reportId}/hydrate-missing-products`
    return this.http
      .post<void>(url, {})
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Checks whether all tracked quantity fields on a row are zero.
   * Uses FPR_QUANTITY_KEYS and treats null, undefined, and empty values as zero.
   * Returns true when none of the tracked fields contain a non-zero value.
   *
   * @param row Row object containing quantity fields to evaluate.
   * @returns True if every tracked quantity field is effectively zero; otherwise false.
   */
  areAllQuantitiesZero(
    row: Partial<Record<FprQuantityKey, number | string | null | undefined>>,
  ): boolean {
    return FPR_QUANTITY_KEYS.every((key) => {
      const value = row[key]
      if (value === null || value === undefined || value === '') {
        return true
      }
      const n = Number(value)
      return Number.isFinite(n) && n === 0
    })
  }

  /**
   * Add a single provisional (external) row to a report's draft rows.
   *
   * @param {string} reportId Report ID
   * @param {ProvisionalRow} payload Provisional row payload
   */
  addProvisionalRow(
    reportId: string,
    payload: ProvisionalRow,
  ): Observable<void> {
    const url = `${this.URL}/${reportId}/provisional-row`

    const isPlastic =
      payload.productMaterialType === PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT

    const row = isPlastic
      ? {
          ...payload.row,
          productMaterialType: PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT,
        }
      : {
          ...payload.row,
          productMaterialType: PRODUCT_MATERIAL_TYPE_RAW_RESIN,
        }

    return this.http
      .post<void>(url, { row })
      .pipe(catchError(this.errorService.handleError))
  }
}
